import { PointColorType } from '@pnext/three-loader';
import { MATPLOTLIB_TO_GRADIENT } from '@/lib/surfaceDifference/shading';
import { atom } from 'jotai';
import { atomFamily, atomWithStorage, createJSONStorage } from 'jotai/utils';
import type { LayerSettings, LayersQuery, PointColorType as GraphqlPointColorType } from '../graphql/graphql';
import { MapLayerLineweight } from '../graphql/graphql';

export type tLayerSettings = Omit<LayerSettings, '__typename'>;
export type LayerData = LayersQuery['layers'][number];

// Atom to store all layers for the current project
export const layersAtom = atom<LayerData[]>([]);

/**
 * Centres derived client-side from loaded geometry, keyed by layer id — a
 * fallback for "Jump to layer center" when `layer.center` was never stored
 * (e.g. LandXML layers created before postprocess computed one). Populated
 * by the renderers (LandXML.tsx) once a layer is parsed.
 */
export const layerRuntimeCentersAtom = atom<Record<string, [number, number, number]>>({});

const layerSelectionStorageKey = (projectId: string) => `@actegon.gis.layers.selected.${projectId}`;

// Atom family to store the selected layer IDs per project with localStorage persistence
const storage = createJSONStorage<string[]>(() => sessionStorage);
// `getOnInit`: read the stored selection on the first render. Without it the
// atom reads [] until it mounts, and the layer list's "first load" default
// selection (useLayers) could run in that window and overwrite a reload's
// restored selection — e.g. switching the images back on under an ortho camera.
export const selectedLayerIdsAtom = atomFamily((projectId: string) =>
  atomWithStorage<string[]>(layerSelectionStorageKey(projectId), [], storage, { getOnInit: true }),
);

// Derived atom to get the selected layers (computed from layers and selectedLayerIds)
export const selectedLayersAtom = atomFamily((projectId: string) =>
  atom((get) => {
    const layers = get(layersAtom);
    const selectedIds = get(selectedLayerIdsAtom(projectId));
    return layers.filter((layer) => selectedIds.includes(layer.id));
  }),
);

// Write-only atom to toggle layer selection
export const toggleLayerSelectionAtom = atomFamily((projectId: string) =>
  atom(null, (get, set, layerId: string) => {
    const selectionAtom = selectedLayerIdsAtom(projectId);
    const currentSelection = get(selectionAtom);
    const isSelected = currentSelection.includes(layerId);

    if (isSelected) {
      // Remove from selection
      set(
        selectionAtom,
        currentSelection.filter((id) => id !== layerId),
      );
    } else {
      // Add to selection
      set(selectionAtom, [...currentSelection, layerId]);
    }
  }),
);

// Write-only atom to set all layers (typically called after fetching from API)
export const setLayersAtom = atom(null, (_get, set, layers: LayerData[]) => {
  set(layersAtom, layers);
});

// Write-only atom to clear layer selection
export const clearLayerSelectionAtom = atomFamily((projectId: string) =>
  atom(null, (_get, set) => {
    set(selectedLayerIdsAtom(projectId), []);
  }),
);

// Write-only atom to select all layers
export const selectAllLayersAtom = atomFamily((projectId: string) =>
  atom(null, (get, set) => {
    const layers = get(layersAtom);
    set(
      selectedLayerIdsAtom(projectId),
      layers.map((layer) => layer.id),
    );
  }),
);

// Derived atom to check if a specific layer is selected
export const isLayerSelectedAtom = atomFamily((projectId: string) =>
  atom((get) => (layerId: string) => {
    const selectedIds = get(selectedLayerIdsAtom(projectId));
    return selectedIds.includes(layerId);
  }),
);

export const layerSettingsStorageKey = (layerId: string) => `@actegon.gis.layer.settings.${layerId}`;
export const layerSettingsAtom = atomFamily((layerId: string) =>
  atomWithStorage<tLayerSettings | null>(
    layerSettingsStorageKey(layerId),
    null,
    createJSONStorage<tLayerSettings | null>(() => localStorage),
  ),
);

export const defaultLayerSettings: tLayerSettings = {
  pointCloudMaterialColorSource: PointColorType.DEPTH as any,
  pointCloudMaterialColor: 'GRAYSCALE' as const,
  pointCloudFixedColor: '#FF0000',
  modelIsWireframe: false,
  modelDefaultColor: '#777777',
  modelOpacity: 1.0,
  modelColorBy: 'DEFAULT',
  modelClassColors: [],
  landxmlCategoryColors: {
    surface: '#a08060',
    alignment: '#e53935',
    parcel: '#43a047',
    breakline: '#787878',
    pipe: '#8b6914',
    structure: '#607d8b',
  },
  landxmlSurfaceColorMode: 'SOLID',
  landxmlSurfaceColormap: 'SPECTRAL' as const,
  pointCloudClasses: new Array(256).fill(null).map(() => ({ isEnabled: true, color: '#000000' })),
  imagePointColor: '#FF69B4',
  imageBrightness: 0,
  imageContrast: 0,
  imageSaturation: 0,
  imageOpacity: 1.0,
  vectorDefaultColor: '#000000',
  vectorDefaultOpacity: 1.0,
  vectorIsWireframe: false,
  vectorDefaultPointSize: 0.15,
  vectorLineRenderAsPipe: false,
  vectorLineWidth: 0.02,
  vectorMapLineweight: MapLayerLineweight.Cad,
  vectorMapLineWidthPx: 1,
  // 'SOLID' (default) | 'ELEVATION' | 'SLOPE'. Mirrors the LandXML
  // surface coloring controls so users can drape a colormap over any
  // vector TIN (rainfall TIN sources, hand-drawn surfaces, imported
  // triangle GeoJSONs).
  vectorSurfaceColorMode: 'SOLID',
  vectorSurfaceColormap: 'SPECTRAL' as const,
  rasterOpacity: 0.8,
  rasterContrast: 0,
  rasterSaturation: 0,
  rasterBrightness: 0,
  scalarRangeMin: null,
  scalarRangeMax: null,
  // Replaced per layer by `layerDefaultSettings`: a surface-difference GLB
  // starts on the colormap its run was made with.
  surfaceDifferenceColormap: 'SPECTRAL' as const,
  // Per-layer transform. Translation in metres in project CRS, rotation
  // in degrees applied as Euler XYZ at render time. (0,0,0,0,0,0) = identity.
  offsetX: 0,
  offsetY: 0,
  offsetZ: 0,
  rotationX: 0,
  rotationY: 0,
  rotationZ: 0,
  sectionOverlays: [],
};

/**
 * Returns layer-specific default settings for a layer whose REMOTE settings
 * have not resolved yet. Any layer shaded by a scalar defaults to
 * `PointColorType.CUSTOM_SCALAR` (51) plus that scalar's gradient — the
 * analysis exists to be seen, and the first frame is the one the user judges
 * it by, so falling back to DEPTH/GRAYSCALE there renders a grey depth cloud
 * where a deviation or clearance result should be.
 */
const SCALAR_GRADIENTS: Record<string, GraphqlPointColorType> = {
  Deviation: 'SPECTRAL',
  // Not SPECTRAL: the clearance ramp puts amber exactly on the profile
  // boundary — see `STOPS.CLEARANCE` in `@/lib/colormaps`.
  Clearance: 'CLEARANCE',
};

export const layerDefaultSettings = (layer: LayerData | null | undefined): tLayerSettings => {
  const meta = layer?.metadata as
    | { scalarAttribute?: unknown; surfaceDifference?: { colormap?: unknown } }
    | null
    | undefined;
  const scalar = meta?.scalarAttribute;
  const gradient = typeof scalar === 'string' ? SCALAR_GRADIENTS[scalar] : undefined;
  if (gradient) {
    return {
      ...defaultLayerSettings,
      pointCloudMaterialColorSource: PointColorType.CUSTOM_SCALAR,
      pointCloudMaterialColor: gradient,
    };
  }
  // A surface-difference result opens on the colormap its run was made with,
  // so the client repaint (GLBModel) reproduces the baked look until the user
  // changes something.
  const sdColormap = meta?.surfaceDifference?.colormap;
  const sdGradient = typeof sdColormap === 'string' ? MATPLOTLIB_TO_GRADIENT[sdColormap] : undefined;
  if (sdGradient) return { ...defaultLayerSettings, surfaceDifferenceColormap: sdGradient as GraphqlPointColorType };
  return defaultLayerSettings;
};

/**
 * Returns `settings` with the per-layer transform (offset + rotation)
 * taken from `transformSource` instead. Used by the bulk "Apply to
 * multiple layers" flow in the layer settings dialog: appearance settings
 * are copied to the targets, but each target keeps its own position —
 * blindly copying the source layer's offset would silently move data.
 */
export const withPreservedTransform = (
  settings: tLayerSettings,
  transformSource: Pick<tLayerSettings, 'offsetX' | 'offsetY' | 'offsetZ' | 'rotationX' | 'rotationY' | 'rotationZ' | 'sectionOverlays'>,
): tLayerSettings => ({
  ...settings,
  offsetX: transformSource.offsetX ?? 0,
  offsetY: transformSource.offsetY ?? 0,
  offsetZ: transformSource.offsetZ ?? 0,
  rotationX: transformSource.rotationX ?? 0,
  rotationY: transformSource.rotationY ?? 0,
  rotationZ: transformSource.rotationZ ?? 0,
  // Overlays belong to ONE result layer's section; "apply to all layers of
  // this type" must never copy them (spec §7.1).
  sectionOverlays: transformSource.sectionOverlays ?? [],
});

// Write-only atom: apply one settings object to many layers' local
// (per-user) settings atoms, preserving each target's own effective
// transform. Local resolution order mirrors useLayerSettings:
// local atom → remote layer.settings → per-layer defaults.
export const applyLayerSettingsToLayersAtom = atom(
  null,
  (get, set, { layerIds, settings }: { layerIds: string[]; settings: tLayerSettings }) => {
    const layers = get(layersAtom);
    for (const layerId of layerIds) {
      const layer = layers.find((l) => l.id === layerId);
      const effective = get(layerSettingsAtom(layerId)) || layer?.settings || layerDefaultSettings(layer);
      set(layerSettingsAtom(layerId), withPreservedTransform(settings, effective));
    }
  },
);

// Create a stable atom that takes layerIds as a parameter
export const layerSettingsManyAtom = atom((get) => (layerIds: string[]) => {
  // One id → layer map per call, not a `find` per layer: callers pass every
  // visible layer on every render (O(layers²) on a 254-layer project).
  const layersById = new Map(get(layersAtom).map((l) => [l.id, l]));
  return layerIds.reduce(
    (acc, layerId) => {
      const layer = layersById.get(layerId);
      const settings = get(layerSettingsAtom(layerId));
      // The REMOTE object is the `layers` query's selection set, which does not
      // select every settings field (`imageBrightness` and its siblings are
      // absent), so it is not a `tLayerSettings` on its own — it is a partial.
      // Completing it from the layer's own defaults is both what makes it one
      // and what every reader already assumed it was; returning the partial
      // left those keys `undefined` at the call site with nothing to say so.
      acc[layerId] = settings || (layer?.settings ? { ...layerDefaultSettings(layer), ...layer.settings } : null);
      return acc;
    },
    {} as Record<string, tLayerSettings | null>,
  );
});
